import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, fetchAdminCustomerDetailMerged } from "@/lib/queryClient";
import { useAdminServicesCatalog, useAdminStaffCatalog } from "@/hooks/useAdminPagedCatalogs";
import { useToast } from "@/hooks/useToast";
import { useParams, useNavigate, Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import {
  ArrowBack,
  Email,
  Phone,
  CalendarMonth,
  AttachMoney,
  Person,
  TrendingUp,
  Loop,
  StarBorder,
  Cached,
  Save,
  LocalOffer,
  Add,
} from "@mui/icons-material";
import { getInitials, formatCents, formatDateTime, getStatusChipColor } from "@/lib/utils";
import {
  bookingToEditPreset,
  resolveOfferingName,
  type CustomerDetailResponse,
} from "@/lib/adminApi";
import type { BookingWithDetails } from "@shared/schema";
import BookingDetailModal from "@/components/BookingDetailModal";
import NewBookingDrawer from "@/components/NewBookingDrawer";

const TAG_PALETTE = ["primary", "secondary", "success", "warning", "info", "error"] as const;

function tagColor(tag: string): (typeof TAG_PALETTE)[number] {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = ((hash << 5) - hash + tag.charCodeAt(i)) | 0;
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
}

export default function AdminCustomerDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetails | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [rebookOpen, setRebookOpen] = useState(false);
  const [rebookPreset, setRebookPreset] = useState<{ serviceId?: string; staffId?: string; customerId?: string } | undefined>();

  const { data: detail, isLoading } = useQuery<CustomerDetailResponse>({
    queryKey: ["/api/admin/customers", id, "detail"],
    queryFn: () => fetchAdminCustomerDetailMerged(id!),
    enabled: !!id,
  });

  const customer = detail?.customer;
  const customerBookings = (detail?.bookings ?? []).sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());

  const { staff: staffList } = useAdminStaffCatalog();
  const { services: servicesCatalog } = useAdminServicesCatalog();

  useEffect(() => {
    if (!customer) return;
    setNotes(customer.notes || "");
    const rawTags = (customer as CustomerDetailResponse["customer"]).tags;
    const arr = Array.isArray(rawTags) ? rawTags.filter((t): t is string => typeof t === "string") : [];
    setTags(arr);
    setMarketingConsent(Boolean((customer as CustomerDetailResponse["customer"]).marketingConsent));
  }, [customer?.id]);

  const totalSpend = customerBookings
    .filter((b) => b.status === "completed" || b.status === "confirmed")
    .reduce((sum, b) => sum + (b.service?.priceCents ?? b.priceCents ?? 0), 0);
  const completedCount = customerBookings.filter((b) => b.status === "completed").length;
  const cancelledCount = customerBookings.filter((b) => b.status === "cancelled").length;

  const avgFreqDisplay = useMemo(() => {
    const completed = customerBookings
      .filter((b) => b.status === "completed")
      .map((b) => new Date(b.startAt).getTime())
      .sort((a, b) => a - b);
    if (completed.length < 2) return t("adminCustomerDetail.notEnoughData");
    let totalGap = 0;
    for (let i = 1; i < completed.length; i++) totalGap += completed[i] - completed[i - 1];
    const avgDays = Math.round(totalGap / (completed.length - 1) / 86400000);
    return t("adminCustomerDetail.everyNDays", { count: avgDays });
  }, [customerBookings, t]);

  const lastSvcDisplay = useMemo(() => {
    const done = customerBookings
      .filter((b) => b.status === "completed")
      .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
    const first = done[0];
    return (first && resolveOfferingName(first, servicesCatalog)) || t("common.emDash");
  }, [customerBookings, t, servicesCatalog]);

  const preferredStaffName = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const b of customerBookings) {
      if (b.staffMember?.displayName) counts[b.staffMember.displayName] = (counts[b.staffMember.displayName] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : t("common.emDash");
  }, [customerBookings, t]);

  const preferredServiceName = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const b of customerBookings) {
      const n = resolveOfferingName(b, servicesCatalog);
      if (n) counts[n] = (counts[n] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : t("common.emDash");
  }, [customerBookings, t, servicesCatalog]);

  const statCards = useMemo(
    () => [
      { labelKey: "adminCustomerDetail.stats.totalBookings" as const, value: String(customerBookings.length), icon: CalendarMonth },
      { labelKey: "adminCustomerDetail.stats.totalSpend" as const, value: formatCents(totalSpend), icon: AttachMoney },
      { labelKey: "adminCustomerDetail.stats.completed" as const, value: String(completedCount), icon: Person },
      { labelKey: "adminCustomerDetail.stats.cancelled" as const, value: String(cancelledCount), icon: Person },
      { labelKey: "adminCustomerDetail.stats.avgFrequency" as const, value: avgFreqDisplay, icon: TrendingUp },
      { labelKey: "adminCustomerDetail.stats.lastService" as const, value: lastSvcDisplay, icon: Loop },
    ],
    [customerBookings.length, totalSpend, completedCount, cancelledCount, avgFreqDisplay, lastSvcDisplay, t],
  );

  const updateMutation = useMutation({
    mutationFn: (data: Partial<{ notes: string; tags: string[]; marketingConsent: boolean }>) =>
      apiRequest("PATCH", `/api/admin/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/customers", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/customers"] });
      toast({ title: t("toasts.saved") });
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  function addTag() {
    const tag = newTag.trim();
    if (!tag || tags.includes(tag)) return;
    const next = [...tags, tag];
    setTags(next);
    setNewTag("");
    updateMutation.mutate({ tags: next });
  }

  function removeTag(tag: string) {
    const next = tags.filter((x) => x !== tag);
    setTags(next);
    updateMutation.mutate({ tags: next });
  }

  function toggleConsent(val: boolean) {
    setMarketingConsent(val);
    updateMutation.mutate({ marketingConsent: val });
  }

  function handleRebook(booking: BookingWithDetails) {
    setRebookPreset({
      serviceId: booking.serviceId,
      staffId: booking.staffId || undefined,
      customerId: booking.customerId,
    });
    setRebookOpen(true);
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Skeleton height={40} width={200} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={240} />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">Customer not found</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/admin/customers")} startIcon={<ArrowBack />}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Button component={Link} to="/admin/customers" startIcon={<ArrowBack />} sx={{ mb: 2 }} data-testid="button-back-customers">
        {t("adminCustomerDetail.backLink")}
      </Button>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Avatar sx={{ width: 64, height: 64, fontSize: "1.25rem" }}>{getInitials(`${customer.firstName} ${customer.lastName}`)}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} data-testid="text-customer-fullname">
            {customer.firstName} {customer.lastName}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5, typography: "body2", color: "text.secondary" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Email fontSize="small" />
              {customer.email}
            </Box>
            {customer.phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Phone fontSize="small" />
                {customer.phone}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 1.5, mb: 3 }}>
        {statCards.map(({ labelKey, value, icon: Icon }) => (
          <Card key={labelKey} variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary", mb: 0.5 }}>
                <Icon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{t(labelKey)}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={700}>
                {value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {customerBookings.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3, typography: "body2", color: "text.secondary" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StarBorder fontSize="small" />
            {t("adminCustomerDetail.preferredStylist", { name: preferredStaffName })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Cached fontSize="small" />
            {t("adminCustomerDetail.favouriteServiceLine", { name: preferredServiceName })}
          </Box>
        </Box>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title={t("adminCustomerDetail.tagsTitle")} avatar={<LocalOffer color="action" />} titleTypographyProps={{ variant: "subtitle1" }} />
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => removeTag(tag)}
                color={tagColor(tag)}
                size="small"
                data-testid={`tag-${tag}`}
              />
            ))}
            {tags.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t("adminCustomerDetail.noTags")}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Add tag (e.g. VIP)"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              data-testid="input-new-tag"
            />
            <IconButton onClick={addTag} data-testid="button-add-tag" color="primary">
              <Add />
            </IconButton>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography fontWeight={600}>{t("adminCustomerDetail.marketingEmailsTitle")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("adminCustomerDetail.marketingEmailsHint")}
            </Typography>
          </Box>
          <Switch checked={marketingConsent} onChange={(_, v) => toggleConsent(v)} data-testid="switch-marketing-consent" />
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title={t("adminCustomerDetail.notesTitle")} />
        <CardContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("adminCustomerDetail.notesPlaceholder")}
            data-testid="textarea-customer-notes"
          />
          <Button sx={{ mt: 1 }} variant="contained" startIcon={<Save />} onClick={() => updateMutation.mutate({ notes })} disabled={updateMutation.isPending} data-testid="button-save-notes">
            Save Notes
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader title={t("adminCustomerDetail.historyTitle")} />
        <CardContent>
          {customerBookings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              {t("adminCustomerDetail.noBookings")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {customerBookings.map((booking) => (
                <Box
                  key={booking.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                  data-testid={`row-booking-${booking.id}`}
                >
                  <Box
                    sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    onClick={() => {
                      setSelectedBooking(booking);
                      setDetailOpen(true);
                    }}
                  >
                    <Typography fontWeight={600}>
                      {resolveOfferingName(booking, servicesCatalog) ?? (
                        <em>{t("adminBookings.serviceRemoved")}</em>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.staffMember?.displayName && `${booking.staffMember.displayName} · `}
                      {formatDateTime(booking.startAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography fontWeight={600}>{formatCents(booking.service?.priceCents ?? booking.priceCents ?? 0, booking.currency || "GBP")}</Typography>
                    <Chip size="small" label={booking.status} color={getStatusChipColor(booking.status)} />
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<Cached />} onClick={() => handleRebook(booking)} data-testid={`button-rebook-${booking.id}`}>
                    {t("adminCustomerDetail.rebook")}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <BookingDetailModal
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        staffList={staffList}
        onEdit={(b) => {
          setRebookPreset(bookingToEditPreset(b));
          setDetailOpen(false);
          setRebookOpen(true);
        }}
      />
      <NewBookingDrawer
        open={rebookOpen}
        onOpenChange={(o) => {
          setRebookOpen(o);
          if (!o) setRebookPreset(undefined);
        }}
        preset={rebookPreset}
      />
    </Box>
  );
}
